/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import {
  Pencil1Icon,
  TrashIcon,
  LockClosedIcon,
} from '@/shared/ui/radix-icons';
import TextInput from '@/shared/ui/components/form/inputs/TextInput';
import { FORM_FIELD_LIMITS } from '@/shared/utils/form-field-limits';
import { SELECTABLE_PALETTE } from '@features/agenda/utils/calendar-display';
import Button from '@/shared/ui/components/basic/Button';
import { Text, UnstyledButton } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';

type Translate = (key: string, vars?: Record<string, string>) => string;

export interface CalendarSwatchProps {
  color: string;
  /** Google calendars are square, ours are round: shape carries the origin. */
  external?: boolean;
}

/**
 * The colour mark at the head of a calendar row.
 *
 * @param props - Colour and whether the calendar came from Google.
 * @returns The rendered swatch.
 */
export const CalendarSwatch: React.FC<CalendarSwatchProps> = ({
  color,
  external = false,
}) => (
  <Text
    component="span"
    aria-hidden="true"
    className={cn(
      'block shrink-0',
      external ? 'rounded-(--radius-sm)' : 'rounded-full',
    )}
    style={{
      background: color,
      height: 'var(--calendar-swatch-size)',
      width: 'var(--calendar-swatch-size)',
    }}
  />
);

export interface CalendarRowProps {
  name: string;
  color: string;
  meta?: string;
  badge?: React.ReactNode;
  external?: boolean;
  t: Translate;
  onEdit?: () => void;
  onDelete?: () => void;
}

const ROW = cn(
  'flex items-center gap-(--space-3) px-(--space-3) py-(--space-2-5)',
  'font-[family-name:var(--font-family)]',
);

/**
 * A calendar at rest: colour, name, an optional explanation and badge, and
 * the two actions when the user may manage it.
 *
 * The name is never truncated. A calendar people named themselves is the one
 * thing on the row they scan for, and a row whose name is cut reads as a
 * different calendar.
 *
 * @param props - Row content and handlers.
 * @returns The rendered row.
 */
export const CalendarRow: React.FC<CalendarRowProps> = ({
  name,
  color,
  meta,
  badge,
  external = false,
  t,
  onEdit,
  onDelete,
}) => (
  <div className={ROW}>
    <CalendarSwatch color={color} external={external} />
    <div className="flex min-w-0 flex-1 flex-col gap-(--space-0-5)">
      <Text
        component="span"
        className="text-[length:var(--text-body-sm)] font-[weight:var(--font-semibold)] text-[var(--text-primary)]"
      >
        {name}
      </Text>
      {meta && (
        <Text
          component="span"
          className="text-[length:var(--text-meta)] text-[var(--text-tertiary-soft)] text-pretty"
        >
          {meta}
        </Text>
      )}
    </div>
    {badge}
    {onEdit && (
      <UnstyledButton
        type="button"
        onClick={onEdit}
        aria-label={t('calendar_settings.edit_aria', { name })}
        className="shrink-0 rounded-(--radius-sm) p-(--space-1) text-[var(--text-tertiary)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)]"
      >
        <Pencil1Icon width={14} height={14} />
      </UnstyledButton>
    )}
    {onDelete && (
      <UnstyledButton
        type="button"
        onClick={onDelete}
        aria-label={t('calendar_settings.delete_aria', { name })}
        className="shrink-0 rounded-(--radius-sm) p-(--space-1) text-[var(--text-tertiary)] hover:bg-[var(--button-danger-ghost-hover-bg)] hover:text-[var(--status-error-text)]"
      >
        <TrashIcon width={14} height={14} />
      </UnstyledButton>
    )}
  </div>
);

/**
 * The locked badge on the institutional calendar.
 *
 * @param props - The label to show.
 * @returns The rendered badge.
 */
export const FixedBadge: React.FC<{ label: string }> = ({ label }) => (
  <Text
    component="span"
    className="flex shrink-0 items-center gap-(--space-1) rounded-(--radius-sm) bg-[var(--surface-tertiary)] px-(--space-2) py-(--space-0-5) text-[length:var(--text-tag)] font-[weight:var(--font-medium)] text-[var(--text-tertiary)]"
  >
    <LockClosedIcon width={10} height={10} aria-hidden="true" />
    {label}
  </Text>
);

export interface CalendarRowEditorProps {
  name: string;
  color: string;
  submitLabel: string;
  saving?: boolean;
  t: Translate;
  onNameChange: (name: string) => void;
  onColorChange: (color: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

/**
 * The editor that takes a row's place.
 *
 * Creating and renaming a calendar ask for the same two things, so they are
 * the same control: it opens at the top of the list to create, and in place
 * of a row to rename. Editing where the row sits keeps the calendar the user
 * clicked under the cursor instead of sending them to a panel below the list.
 *
 * @param props - Draft values, labels and handlers.
 * @returns The rendered editor.
 */
export const CalendarRowEditor: React.FC<CalendarRowEditorProps> = ({
  name,
  color,
  submitLabel,
  saving = false,
  t,
  onNameChange,
  onColorChange,
  onSubmit,
  onCancel,
}) => (
  <div className="flex flex-col gap-(--space-3) bg-[var(--surface-card)] px-(--space-3) py-(--space-3)">
    <TextInput
      name="calendar-name"
      autoFocus
      placeholder={t('calendar_settings.new_placeholder')}
      aria-label={t('calendar_settings.name_aria')}
      value={name}
      maxLength={FORM_FIELD_LIMITS.calendarName}
      onChange={(e) => onNameChange(e.target.value)}
    />
    <div className="flex flex-wrap items-center justify-between gap-(--space-3)">
      <div className="flex gap-(--space-2)">
        {SELECTABLE_PALETTE.map((c) => (
          <UnstyledButton
            key={c}
            type="button"
            onClick={() => onColorChange(c)}
            aria-label={t('manager.pick_color', { color: c })}
            aria-pressed={color === c}
            className="h-5 w-5 rounded-full border-2 transition-transform hover:scale-110"
            style={{
              background: c,
              borderColor: color === c ? 'var(--text-primary)' : 'transparent',
            }}
          >
            {null}
          </UnstyledButton>
        ))}
      </div>
      <div className="flex items-center gap-(--space-2)">
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          {t('actions.cancel')}
        </Button>
        <Button
          type="button"
          variant="solid"
          size="sm"
          loading={saving}
          disabled={saving || !name.trim()}
          onClick={onSubmit}
        >
          {submitLabel}
        </Button>
      </div>
    </div>
  </div>
);
